/**
 * ErrorBoundary — Cycle 45.
 *
 * React component render/lifecycle 에서 throw 발생 시 graceful fallback UI.
 * Sentry 미설치 환경 — console.error 로 stack 로깅 (운영 환경에서는 별도 logging 도입 검토).
 *
 * 정책:
 *   - class component (React 의 error boundary 는 hook 미지원)
 *   - fallback: 안내 텍스트 + reload 버튼 (window.location.reload)
 *   - error 본문 (message) 만 표시 — stack 은 console 로만 (사용자 노출 안 함)
 *   - lang-aware 메시지 (cycle 45 lang switcher 지원 — base-i18n 의 lang)
 */

import { Component, createRef, type ReactNode, type ErrorInfo } from 'react';
import { getSharedLang } from '../lib/base-i18n';
import { reportError } from '../lib/telemetry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
}

const MESSAGES = {
  ko: {
    title: '오류가 발생했습니다',
    body: '예상치 못한 오류로 화면을 표시할 수 없습니다.',
    retry: '다시 시도',
    details_label: '오류 상세',
  },
  en: {
    title: 'Something went wrong',
    body: 'An unexpected error occurred and the page cannot be displayed.',
    retry: 'Retry',
    details_label: 'Error details',
  },
  ja: {
    title: 'エラーが発生しました',
    body: '予期しないエラーが発生したため、画面を表示できません。',
    retry: '再試行',
    details_label: 'エラー詳細',
  },
} as const;

export class ErrorBoundary extends Component<Props, State> {
  // Cycle 50 — fallback UI 의 첫 focusable (재시도 button) ref. componentDidCatch
  // 또는 componentDidUpdate 에서 자동 focus → screen reader 와 keyboard 사용자가
  // fallback 진입 시점을 즉시 인지. cycle 35 modal focus 패턴의 ErrorBoundary 변형.
  private retryButtonRef = createRef<HTMLButtonElement>();

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message ?? 'Unknown error' };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Cycle 46 — telemetry endpoint 로 best-effort 전송. DEV 환경은 console.error 만,
    // PROD 환경은 fetch POST /api/v1/internal/telemetry/error (silent fail, rate-limited).
    reportError(error, {
      componentStack: info.componentStack ?? undefined,
      source: 'react-error-boundary',
    });
    // Cycle 50 — a11y: error 진입 직후 retry button 에 자동 focus. componentDidCatch
    // 는 React 가 fallback render 완료 후 호출되므로 ref.current 가 button 보장.
    // keyboard 사용자가 tab 없이 즉시 "다시 시도" 누를 수 있고 screen reader 도 focus
    // 위치 알림 (role="alert" live region 보완).
    queueMicrotask(() => this.retryButtonRef.current?.focus());
  }

  reset = (): void => {
    // 단순 reload — state reset 으로 partial recovery 도 가능하지만
    // 안전을 위해 전체 reload (data fetch / store 모두 초기화).
    if (typeof window !== 'undefined') window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const lang = getSharedLang();
    const m = MESSAGES[lang] ?? MESSAGES.en;

    // Cycle 51 — a11y 보강:
    //   - aria-labelledby = title id (h1) → screen reader 가 fallback container 명칭 알림
    //   - aria-describedby = body id (p) → 안내 문구 자동 음독
    //   - pre 의 aria-label = '오류 상세' (lang 별) → message redaction (raw error.message 도
    //     screen reader 가 '오류 상세' 후 읽음 — 사용자는 기술 내용 인지 가능)
    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        aria-labelledby="error-boundary-title"
        aria-describedby="error-boundary-body"
        style={{
          maxWidth: '480px',
          margin: '4rem auto',
          padding: '2rem',
          border: '1px solid var(--color-border, #e5e7eb)',
          borderRadius: '8px',
          background: 'var(--color-surface, #fff)',
          textAlign: 'center',
        }}
      >
        <h1 id="error-boundary-title" style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>{m.title}</h1>
        <p id="error-boundary-body" style={{ color: 'var(--color-text-secondary, #6b7280)', marginBottom: '1.5rem' }}>{m.body}</p>
        {this.state.message && (
          <pre
            data-testid="error-boundary-message"
            aria-label={m.details_label}
            style={{
              fontSize: '0.75rem',
              color: 'var(--color-danger, #b91c1c)',
              background: 'var(--color-danger-bg, #fef2f2)',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              overflow: 'auto',
              maxHeight: '120px',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
            }}
          >
            {this.state.message}
          </pre>
        )}
        <button
          type="button"
          ref={this.retryButtonRef}
          data-testid="error-boundary-reload"
          onClick={this.reset}
          style={{
            padding: '0.5rem 1.25rem',
            background: 'var(--color-brand, #4f46e5)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          {m.retry}
        </button>
      </div>
    );
  }
}
